import type { Action, Dispatch, TypedStartListening } from '@reduxjs/toolkit';

import { authActions } from '../../flux/auth';
import { institutionLoginsActions, institutionLoginsSelectors } from '../../flux/institution-logins';
import type { INSTITUTION_LOGINS_REDUCER_KEY } from '../../flux/institution-logins/institutionLoginsTypes';
import type RootState from '../../utils/redux-store/rootState';

// State must include the institution logins store
type RequiredState = Pick<RootState, typeof INSTITUTION_LOGINS_REDUCER_KEY>;
type AppStartListening = TypedStartListening<RequiredState, Dispatch<Action>>;

/**
 * Setup pauthDetection listener
 *
 * This listener monitors institution login updates and detects when an institution
 * login transitions to PAUTH_INVALID_TOKEN status for the first time. It compares
 * the previous state (before the action) with the current state (after the action)
 * to identify new PAUTH errors.
 *
 * When a transition is detected, it dispatches pauthInvalidTokenDetected action
 * which triggers the ReauthDialog flow.
 *
 * @param startAppListening - The platform-specific startAppListening function
 */
const PAUTH_INVALID_TOKEN_STATUS = 'PAUTH_INVALID_TOKEN';

// Track which institution logins we've already dispatched for to prevent duplicates
const dispatchedForLoginIds = new Set<string>();

/** @internal Exported for testing only - clears the dispatched tracking set */
export const _resetDispatchedForLoginIds = () => {
  dispatchedForLoginIds.clear();
};

export const setupPauthDetectionListener = (startAppListening: AppStartListening) => {
  // Clear tracking set on logout or dataset switch to avoid stale IDs in new sessions
  startAppListening({
    predicate: (action) =>
      action.type === authActions.authLogout.type || action.type === authActions.authSelectDataset.type,
    effect() {
      dispatchedForLoginIds.clear();
    },
  });

  // Main listener for PAUTH detection
  startAppListening({
    predicate: (action, _currentState, _previousState) => {
      // Listen to any action that might update institution logins
      const matches =
        action.type === institutionLoginsActions.getInstitutionLoginsSuccess.type ||
        action.type === institutionLoginsActions.updateInstitutionLoginSuccess.type ||
        action.type === institutionLoginsActions.qcsInstitutionLoginActionResponse.type ||
        action.type === institutionLoginsActions.updateChangedInstitutionLogins.type ||
        action.type === institutionLoginsActions.batchInstitutionLoginsSuccess.type;

      return matches;
    },
    effect(action, listenerApi) {
      const currentState = listenerApi.getState();
      const previousState = listenerApi.getOriginalState(); // State BEFORE the action

      const currentLoginsById = institutionLoginsSelectors.getInstitutionLoginsById(currentState);
      const previousLoginsById = institutionLoginsSelectors.getInstitutionLoginsById(previousState);

      if (!currentLoginsById) {
        return;
      }

      // Iterate through all current logins and check for PAUTH transitions
      currentLoginsById.forEach((currentLogin) => {
        if (!currentLogin.id) return;

        const currentAggStatus = String(currentLogin.aggregators?.get?.(0)?.aggStatus || '');
        const previousLogin = previousLoginsById?.get(currentLogin.id);
        const previousAggStatus = String(previousLogin?.aggregators?.get?.(0)?.aggStatus || '');

        // Detect FIRST TIME transition to PAUTH_INVALID_TOKEN
        const isTransitionToPauth =
          currentAggStatus === PAUTH_INVALID_TOKEN_STATUS && previousAggStatus !== PAUTH_INVALID_TOKEN_STATUS;

        // Clear from dispatched set if status is no longer PAUTH_INVALID_TOKEN
        // This allows re-detection if the status returns to PAUTH_INVALID_TOKEN later
        if (currentAggStatus !== PAUTH_INVALID_TOKEN_STATUS) {
          dispatchedForLoginIds.delete(currentLogin.id);
        }

        if (isTransitionToPauth && !dispatchedForLoginIds.has(currentLogin.id)) {
          // Mark as dispatched to prevent duplicate dispatches from rapid actions
          dispatchedForLoginIds.add(currentLogin.id);

          // Dispatch action to notify other parts of the system
          listenerApi.dispatch(
            institutionLoginsActions.pauthInvalidTokenDetected({
              institutionLoginId: currentLogin.id,
              previousStatus: previousAggStatus ?? undefined,
            }),
          );
        }
      });
    },
  });
};
